import { handleActions } from 'redux-actions';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import _get from 'lodash/get';
import _xor from 'lodash/xor';

import moment from 'moment';
import qs from 'qs';
import { getFormValues, reset, change } from 'redux-form';

import checkIsExcludedCountryCode from '../shared/checkIsExcludedCountryCode';
import http from '../shared/http';
import endpoints from '../../config/endpoints';

import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';

import { default as createCalendar, getDateRangeFromCalendar } from '../shared/calendar';
import createLocalStorageManager from '../shared/local-storage';
import createFilters from '../shared/filters';
import csvExport from '../shared/csvExport';

const isRocket = process.env.CPA_ENV === 'rocket';

const localStorageManager = createLocalStorageManager('conversionsApproveRequests');

const calendar = createCalendar({
	namespace: 'conversionsApproveRequests',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const calendarOfCreationDate = createCalendar({
	namespace: 'conversionsApproveRequests',
	stateNodeName: 'calendarOfCreationDate',
	defaultValue: {
		start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

// actions types
const FETCH_DATA_SUCCESS = 'conversions-approve-requests/fetch-data-success';
const FETCH_DATA_ERROR = 'conversions-approve-requests/fetch-data-error';

const FETCH_REPORT_DATA_SUCCESS = 'conversions-approve-requests/FETCH_REPORT_DATA_SUCCESS';

const TOGGLE_PROGRESS_BAR = 'conversions-approve-requests/toggle-progress-bar';
const SET_MONTHLY_PAYMENTS = 'conversions-approve-requests/set-monthly-payments';

const LOOKUP_SUCCESS = 'conversions-approve-requests/lookup-success';
const LOOKUP_ERROR = 'conversions-approve-requests/lookup-error';

const TOGGLE_EXPANDED_ROWS = 'conversions-approve-requests/TOGGLE_EXPANDED_ROWS';

const REQUEST_MANUAL_APPROVE_SUCCESS = 'conversions-approve-requests/request-manual-approve-success';
const REQUEST_MANUAL_APPROVE_ERROR = 'conversions-approve-requests/request-manual-approve-error';

const FETCH_MANUAL_APPROVE_LEADS_SUCCESS = 'conversions-approve-requests/FETCH_MANUAL_APPROVE_LEADS_SUCCESS';

const CLEAR = 'conversions-approve-requests/clear';
const SAVED_FILTERS = 'conversions-approve-requests/saved-filters';

const RESTRICT_NEW_APPROVE = 'conversions-approve-requests/restrict-new-approve';

const TOGGLE_REQUEST_FOR_APPROVE_POPUP = 'conversions-approve-requests/TOGGLE_REQUEST_FOR_APPROVE_POPUP';

// permissions
export const allowedPermissions = {
	view: 'manual_approve_requests',
	confirm: 'manual_approve_requests_confirm'
};

const filters = createFilters({
	namespace: 'conversionsApproveRequests',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'locations_ids',
			title: 'Location',
			placeholder: 'Add location',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
			}
		},
		{
			name: 'offer_ids',
			title: 'Offers',
			placeholder: 'Add offer',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'affiliate_id',
			title: 'Affiliates',
			placeholder: 'Add affiliate',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'admin_ids',
			title: 'Admins',
			placeholder: 'Add admin',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS
			}
		}]
});

// initial state
const initialState = {
	data: null,
	calendar: calendar.initialState,
	calendarOfCreationDate: calendarOfCreationDate.initialState,
	monthlyPayments: [],
	expandedRows: [],
	isUpdateRequestPopupOpened: false,
	manualApproveInitialValues: {
		affiliates: null,
		offers: null,
		countries: null,
		percent_to_approve: null
	},
	table: {
		columns: {
			manager: {
				index: 'manager',
				title: 'Manager/Affiliate/Geo/Offer',
				className: 'fixed-col'
			},
			interval: {
				index: 'interval',
				title: 'Period for Conversions',
				className: 'approve_request_cell min-width-200'
			},
			created_at: {
				index: 'created_at',
				title: 'Created',
				className: 'approve_request_cell min-width-100'
			},
			toApproveCount: {
				index: 'toApproveCount',
				title: 'Conv count',
				className: 'approve_request_cell min-width-100'
			},
			totalValidCount: {
				index: 'totalValidCount',
				title: 'All valid',
				className: 'approve_request_cell min-width-100'
			},
			pendingCount: {
				index: 'pendingCount',
				title: 'pending',
				className: 'approve_request_cell min-width-100'
			},
			totalPayout: {
				index: 'totalPayout',
				title: 'Payout',
				className: 'approve_request_cell min-width-150'
			},
			sid: {
				index: 'sid',
				title: 'sid',
				className: 'approve_request_cell min-width-150'
			},
			target_approve: {
				index: 'target_approve',
				title: '%% for Approve',
				className: 'approve_request_cell min-width-150'
			},
			approve_after: {
				index: 'approve_after',
				title: 'Approve after',
				className: 'approve_request_cell min-width-150'
			},
			current_approve_ratio_affiliate: {
				index: 'current_approve_ratio_affiliate',
				title: 'Approve for period',
				className: 'approve_request_cell min-width-150'
			},
			approve_for_period: {
				index: 'approve_for_period',
				title: 'Av convers Aff',
				className: 'approve_request_cell min-width-150'
			},
			approve_for_past_period: {
				index: 'approve_for_past_period',
				title: 'Av convers GEO',
				className: 'approve_request_cell min-width-150'
			},
			reason: {
				index: 'reason',
				title: 'Reason',
				className: 'approve_request_cell min-width-150'
			},
			type: {
				index: 'type',
				title: 'Type',
				className: 'approve_request_cell min-width-150'
			},
			status: {
				index: 'status',
				title: 'Status',
				className: 'approve_request_cell min-width-150'
			},
			clickup_task_id: {
				index: 'clickup_task_id',
				title: 'CC Alarm status',
				className: 'approve_request_cell min-width-150'
			},
			margin_per_aff: {
				index: 'margin_per_aff',
				title: 'Margin per Aff',
				className: 'approve_request_cell min-width-150 approve_request_cell_margin_aff_hint'
			},
			margin_per_geo: {
				index: 'margin_per_geo',
				title: 'Margin per GEO',
				className: 'approve_request_cell min-width-150 approve_request_cell_margin_geo_hint'
			},
			comment: {
				index: 'comment',
				title: 'Comment',
				className: 'approve_request_cell min-width-150'
			},
			status_changed_by: {
				index: 'status_changed_by',
				title: 'Changed by',
				className: 'approve_request_cell min-width-150'
			},
			actions: {
				index: 'actions',
				title: 'Actions',
				className: 'fixed-col-right small-coll-100 approve_request_cell min-width-100'
			}
		}
	},
	filters: filters.initialState,
	isRequestForApprovePopupVisible: false,
	showProgressBar: false,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			return request(getState)(dispatch)
				.then(() => request(getState, endpoints.MANUAL_APPROVE_REQUEST.TEAMS_REPORT, true)(dispatch))
				.then(action => dispatch({ type: SET_MONTHLY_PAYMENTS, payload: action.payload.data }));
		},
		getTeamsReport: () => (dispatch, getState) => {
			return request(getState, endpoints.MANUAL_APPROVE_REQUEST.TEAMS_REPORT)(dispatch)
				.then(data => {
					const rows = data.payload.data;
					if (!rows || !rows.length) {
						return;
					}

					const headers = Object.keys(rows[0]);
					const columns = rows.map(entity => headers.map(h => entity[h] || '-'));
					const rowsForExport = [].concat([headers.map(h => h)], columns);

					return csvExport('Teams-Report', rowsForExport);
				});
		},
		openPredefinedForm: (row, e) => (dispatch, getState) => {
			e.preventDefault();

			const range = {
				startDate: moment(row.approve_from),
				endDate: moment(row.approve_to)
			};

			dispatch(calendar.actions.calendarChange(range, calendar.actions.calendarApply));
			dispatch(calendar.actions.calendarChange(range, calendar.actions.calendarApply));

			dispatch(change('manualApproveForm', 'percent_to_approve', row.target_approve));
			dispatch(change('manualApproveForm', 'affiliates', [{ value: row.affiliate_id, label: row.affiliate }]));
			dispatch(change('manualApproveForm', 'offers', [{ value: row.offer_id, label: row.offer }]));
			dispatch(change('manualApproveForm', 'countries', [{ value: row.country_id, label:  row.country}]));
			dispatch(change('manualApproveForm', 'sid', row.sid));

		},
		getReport: () => (dispatch, getState) => {

			const rows = selectors.getData(getState());

			if (!rows || !rows.length) {
				return;
			}

			const headers = Object.keys(rows[0]);
			const columns = rows.map(entity => headers.map(h => entity[h] || '-'));
			const rowsForExport = [].concat([headers.map(h => h)], columns);

			return csvExport('Conversion-Approve-Report', rowsForExport);
		},

		locationsLookup: text => (dispatch, getState) => {
			const formData = getFormValues('manualApproveForm')(getState());
			let url = `${endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC}?query=${text}`;

			if (formData.offers && formData.offers[0] && formData.offers[0].value) {
				url+= `&offers=${formData.offers[0].value}`;
			}

			const request = http({
				url,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					const data = action.payload.data
						.filter(entity => !(isRocket && checkIsExcludedCountryCode(entity.country_code)));

					return { options: data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},

		affiliatesLookup: text => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.LOOKUPS.AFFILIATES}?query=${text}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		updateType: (requestId, formData) => (dispatch) => {
			const type = _get(formData, 'type.0.value');

			const approveRequest = http({
				url: `${endpoints.MANUAL_APPROVE_REQUEST.BASE}/${requestId}/type`,
				method: 'put',
				data: { type, comment: formData.comment },
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR]
			});

			return approveRequest(dispatch)
				.then(() => dispatch(actions.fetchData()));
		},
		applyForApprove: (type, e) => (dispatch, getState) => {
			e.preventDefault();
			const state = getState();
			const date_range = getDateRangeFromCalendar(state.conversionsApproveRequests.calendar);
			const formData = cloneDeep(getFormValues('manualApproveForm')(state));
			const requestApproveFormData = cloneDeep(getFormValues('requestApproveForm')(state));

			const { percent_to_approve, offers, countries, affiliates, sid } = cloneDeep(formData);

			const data = {
				approve_from: moment.utc(date_range.start)
					.hours(0).minutes(0).seconds(0).milliseconds(0)
					.format('YYYY-MM-DDTHH:mm:ss.SSS'),
				approve_to: moment.utc(date_range.end)
					.hours(23).minutes(59).seconds(59).milliseconds(999)
					.format('YYYY-MM-DDTHH:mm:ss.SSS'),
				target_approve: parseInt(percent_to_approve),
				offer_id: _get(offers, '0.value', null),
				country_id: _get(countries, '0.value', null),
				affiliate_id: _get(affiliates, '0.value', null),
				reason: _get(requestApproveFormData, 'reason.0.value', null),
				type: type,
				sid
			};
			if (!data.reason) {
				return;
			}

			dispatch(actions.toggleProgressBar());

			const approveRequest = http({
				url: endpoints.MANUAL_APPROVE_REQUEST.BASE,
				method: 'post',
				data,
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR]
			});

			return approveRequest(dispatch).then(action => {
				if (action.type === REQUEST_MANUAL_APPROVE_SUCCESS) {
					dispatch(actions.fetchData());
					dispatch(reset('manualApproveForm'));
					dispatch({ type: TOGGLE_REQUEST_FOR_APPROVE_POPUP });
				}
			});
		},
		toggleExpandedRow: (id) => ({ type: TOGGLE_EXPANDED_ROWS, payload: id }),
		approve: (id) => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			return http({
				url: `${endpoints.MANUAL_APPROVE_REQUEST.BASE}/:id/approve`.replace(':id', id),
				method: 'put',
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === REQUEST_MANUAL_APPROVE_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		decline: (id) => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			return http({
				url: `${endpoints.MANUAL_APPROVE_REQUEST.BASE}/:id/decline`.replace(':id', id),
				method: 'put',
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === REQUEST_MANUAL_APPROVE_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		remove: (id) => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			return http({
				url: `${endpoints.MANUAL_APPROVE_REQUEST.BASE}/:id`.replace(':id', id),
				method: 'delete',
				actions: [REQUEST_MANUAL_APPROVE_SUCCESS, REQUEST_MANUAL_APPROVE_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === REQUEST_MANUAL_APPROVE_SUCCESS) {
						dispatch(actions.fetchData());
					}
				});
		},
		offersLookup: text => (dispatch, getState) => {
			const hidden_items = _get(getState(), 'root.data.settings.hidden_items');
			const isHiddenRemoved =
				Array.isArray(hidden_items) && hidden_items.includes('conversions-approve-all-offers');

			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS}?${qs.stringify({ query: text, isShowedRemoved: !isHiddenRemoved })}`,
				method: 'get',
				actions: [LOOKUP_SUCCESS, LOOKUP_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},

		getConversionsForApprove: () => (dispatch, getState) => {
			const formData = cloneDeep(getFormValues('manualApproveForm')(getState()));
			const state = getState();
			const date_range = getDateRangeFromCalendar(state.conversionsApproveRequests.calendar);

			let { percent_to_approve, offers, countries, affiliates, sid } = cloneDeep(formData);

			percent_to_approve = parseInt(percent_to_approve);

			const data = {
				approve_from: date_range.start,
				approve_to: date_range.end,
				target_approve: percent_to_approve,
				offer_id: offers && offers.map(i => i.value)[0] || [],
				country_id: countries && countries.map(i => i.value)[0] || [],
				affiliate_id: affiliates && affiliates.map(i => i.value)[0] || [],
				sid
			};

			const approveRequest = http({
				url: `${endpoints.MANUAL_APPROVE_REQUEST.BASE}/preview?${qs.stringify(data)}`,
				method: 'get',
				actions: [ FETCH_MANUAL_APPROVE_LEADS_SUCCESS ]
			});

			return approveRequest(dispatch)
				.then(action => {
					if (action.type === FETCH_MANUAL_APPROVE_LEADS_SUCCESS) {
						return action.payload.data;
					}
				});
		},

		clear: () => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			dispatch({ type: CLEAR });
			request(getState)(dispatch);
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		toggleRequestForApprovePopup: () => ({
			type: TOGGLE_REQUEST_FOR_APPROVE_POPUP
		})
	},
	calendar.actions,
	calendarOfCreationDate.actions,
	filters.actions
);

// reducers
const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			manualApproveInitialValues: !action.meta
				? state.manualApproveInitialValues
				: {
					...state.manualApproveInitialValues,
					...action.meta
				},
			data: action.payload.data.rows,
			showProgressBar: false,
			error: false
		}),
		[CLEAR]: (state, action) => ({ ...state, initialState }),
		[SET_MONTHLY_PAYMENTS]: (state, action) => ({ ...state, monthlyPayments: action.payload || [] }),
		[SAVED_FILTERS]: (state, action) => ({
			...state,
			calendar: initialState.calendar,
			manualApproveInitialValues: {
				...initialState.manualApproveInitialValues,
				...localStorageManager.values(action.payload).filters
			}
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_EXPANDED_ROWS]: (state, action) => ({
			...state, expandedRows: _xor(state.expandedRows, [action.payload])
		}),
		[TOGGLE_REQUEST_FOR_APPROVE_POPUP]: (state, action) => ({
			...state, isRequestForApprovePopupVisible: !state.isRequestForApprovePopupVisible
		}),
		[LOOKUP_ERROR]: state => ({ ...state, showProgressBar: false, error: true }),
		[REQUEST_MANUAL_APPROVE_ERROR]: (state, action) => ({
			...state,
			manualApproveInitialValues: action.meta
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[REQUEST_MANUAL_APPROVE_SUCCESS]: state => ({ ...state, showProgressBar: false }),
		[REQUEST_MANUAL_APPROVE_ERROR]: state => ({ ...state, showProgressBar: false }),
		[RESTRICT_NEW_APPROVE]: state => ({ ...state, showProgressBar: false })
	},
	calendar.reducers,
	calendarOfCreationDate.reducers,
	filters.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.conversionsApproveRequests.data,
	getMonthlyPayments: state => state.conversionsApproveRequests.monthlyPayments,
	getExpandedRows: state => state.conversionsApproveRequests.expandedRows,
	getCalendar: state =>
		calendar.selectors.getCalendar(state => state.conversionsApproveRequests.calendar, state => state)(state),
	getCalendarOfCreationDate: state =>
		calendarOfCreationDate.selectors.getCalendar(
			state => state.conversionsApproveRequests.calendarOfCreationDate, state => state
		)(state),
	getTable: state => {
		const { table, data } = state.conversionsApproveRequests;
		const expandedRowIds = selectors.getExpandedRows(state);
		let rows = state.conversionsApproveRequests.data ? data : [];
		if (expandedRowIds.length) {
			rows = rows.reduce((acc, val) => {
				if (expandedRowIds.includes(val.id) && val.leads_by_date && val.leads_by_date.length) {
					const leads_by_date = val.leads_by_date
						.filter(val => val.toApproveCount > 0)
						.map(item => ({...item, expandedFromId: val.id }));

					acc = [...acc, val, ...leads_by_date ];
				} else {
					acc = [...acc, val];
				}
				return acc;
			}, []);
		}
		return {
			headers: values(table.columns),
			rows: rows,
			sortedColumn: table.sortByColumn
		};
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.conversionsApproveRequests.filters,
		state => state
	)(state),
	getManualApproveInitialValues: state => state.conversionsApproveRequests.manualApproveInitialValues,
	getIntl: state => intl(state),
	getProgressBar: state => state.conversionsApproveRequests.showProgressBar,
	getReasons: state => state.root.lists['manual-approve-reasons'],
	getIsRequestForApprovePopupVisible: state => state.conversionsApproveRequests.isRequestForApprovePopupVisible,
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.conversionsApproveRequests.error
};

const request = ( getState, baseUrl = null, isOnStart = false) => {
	const query = {};

	const calendar = selectors.getCalendarOfCreationDate(getState()).value;
	const selectedFilters = selectors.getFilters(getState()).selectedFilters;
	for (const filter of selectedFilters) {
		if (!filter.value || !filter.value[0] || !filter.name) {
			continue;
		}
		query.filters = query.filters || {};
		query.filters[filter.name]  = filter.value.map(val => val.value);
	}
	query.date_range = { start: `${calendar.start}T00:00:00Z`, end: `${calendar.end}T23:59:59Z` };

	if (isOnStart) {
		query.date_range = {
			start: moment.utc()
				.date(1).hours(0).minutes(0).seconds(0).milliseconds(0)
				.format('YYYY-MM-DDTHH:mm:ssZ'),
			end: moment.utc()
				.date(1).hours(0).minutes(0).seconds(0).milliseconds(0)
				.add('1', 'month')
				.date(0).hours(23).minutes(59).seconds(59).milliseconds(999)
				.format('YYYY-MM-DDTHH:mm:ssZ')
		};
	}

	return http({
		url: `${baseUrl || endpoints.MANUAL_APPROVE_REQUEST.BASE}?${qs.stringify(query)}`,
		method: 'get',
		actions: [ baseUrl ? FETCH_REPORT_DATA_SUCCESS : FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/conversions-approve-requests/index.js